import React, { useState, useEffect, useMemo } from 'react';
import styles from './Legal.module.css';

const LegalPage = () => {
  const [activeSection, setActiveSection] = useState('terms');
  const [scrolled, setScrolled] = useState(false);

  /* ✅ STATIC CONFIG (MEMOIZED) */
  const legalSections = useMemo(
    () => [
      { id: 'terms', title: 'Terms & Conditions' },
      { id: 'privacy', title: 'Privacy Policy' },
      { id: 'disclaimer', title: 'Legal Disclaimer' },
      { id: 'kyc', title: 'KYC & Verification' },
      { id: 'lending', title: 'Lending Guidelines' },
      { id: 'dispute', title: 'Dispute Resolution' }
    ],
    []
  );

  const handleSectionClick = (sectionId) => {
    setActiveSection(sectionId);
    const element = document.getElementById(sectionId);

    if (element) {
      const headerOffset = 120;
      const elementPosition = element.getBoundingClientRect().top;
      const offsetPosition =
        elementPosition + window.pageYOffset - headerOffset;

      window.scrollTo({
        top: offsetPosition,
        behavior: 'smooth'
      });
    }
  };

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);

      const sectionElements = legalSections.map((s) =>
        document.getElementById(s.id)
      );

      const scrollPosition = window.scrollY + 150;

      for (let i = sectionElements.length - 1; i >= 0; i--) {
        if (
          sectionElements[i] &&
          scrollPosition >= sectionElements[i].offsetTop
        ) {
          setActiveSection(legalSections[i].id);
          break;
        }
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, [legalSections]); // ✅ ESLint satisfied

  return (
    <div className={styles.legalPage}>
      {/* Header */}
      <div
        className={`${styles.header} ${
          scrolled ? styles.scrolled : ''
        } ${styles.centerHeader}`}
      >
        <h1 className={styles.title}>Legal Documentation</h1>
        <p className={styles.subtitle}>Rupayya F2F Lending Platform</p>
        <p className={styles.lastUpdated}>
          Last Updated: January 18, 2026 | Version 3.0
        </p>
      </div>

      <div className={styles.contentWrapper}>
        {/* Sidebar */}
        <div className={styles.sidebar}>
          <div className={styles.sidebarSticky}>
            <h3 className={styles.sidebarTitle}>Quick Navigation</h3>
            <nav className={styles.nav}>
              {legalSections.map((section) => (
                <button
                  key={section.id}
                  className={`${styles.navButton} ${
                    activeSection === section.id ? styles.active : ''
                  }`}
                  onClick={() => handleSectionClick(section.id)}
                >
                  {section.title}
                </button>
              ))}
            </nav>
          </div>
        </div>

        {/* Main Content */}
        <div className={styles.mainContent}>
          {/* Terms & Conditions */}
          <section id="terms" className={styles.section}>
            <h2 className={styles.sectionTitle}>📄 Terms & Conditions</h2>
            <div className={styles.sectionContent}>
              <h3>1. Platform Overview</h3>
              <p>
                Rupayya ("Rupayya", "Platform")
                operates as a technology-enabled peer-to-peer lending platform
                facilitating loans strictly between individuals with
                pre-existing personal relationships.
              </p>

              <h3>2. Acceptance of Terms</h3>
              <p>
                By accessing or using the platform, you agree to be bound by
                these Terms & Conditions. If you do not agree, you must stop
                using the platform immediately.
              </p>

              <h3>3. Eligibility</h3>
              <ul>
                <li>Minimum age of 18 years</li>
                <li>Resident of India</li>
                <li>Valid bank account</li>
                <li>Completed KYC verification</li>
              </ul>

              <h3>4. User Responsibilities</h3>
              <ul>
                <li>Conduct due diligence on counterparties</li>
                <li>Ensure repayment capability</li>
                <li>Comply with tax regulations</li>
                <li>Keep credentials confidential</li>
              </ul>
            </div>
          </section>

          {/* Privacy Policy */}
          <section id="privacy" className={styles.section}>
            <h2 className={styles.sectionTitle}>🔒 Privacy Policy</h2>
            <div className={styles.sectionContent}>
              <h3>1. Information Collected</h3>
              <ul>
                <li>Personal details (Name, PAN, Aadhaar)</li>
                <li>Banking & transaction data</li>
                <li>Device & technical data</li>
              </ul>

              <h3>2. Use of Information</h3>
              <ul>
                <li>KYC & identity verification</li>
                <li>Transaction processing</li>
                <li>Fraud prevention & security</li>
                <li>Legal compliance</li>
              </ul>

              <h3>3. Data Protection</h3>
              <p>
                We use encryption, access controls, and periodic security audits
                to protect your data.
              </p>
            </div>
          </section>

          {/* Legal Disclaimer */}
          <section id="disclaimer" className={styles.section}>
            <h2 className={styles.sectionTitle}>⚠️ Legal Disclaimer</h2>
            <div className={styles.sectionContent}>
              <p>
                Rupayya is not a bank, NBFC, or financial institution. The
                platform does not guarantee repayments or recovery of funds.
              </p>

              <ul>
                <li>No lending guarantees</li>
                <li>No recovery responsibility</li>
                <li>No financial advice</li>
              </ul>
            </div>
          </section>

          {/* KYC & Verification */}
          <section id="kyc" className={styles.section}>
            <h2 className={styles.sectionTitle}>✅ KYC & Verification</h2>
            <div className={styles.sectionContent}>
              <ul>
                <li>PAN & Aadhaar verification</li>
                <li>Bank account verification</li>
                <li>Video KYC for higher limits</li>
                <li>AML compliance monitoring</li>
              </ul>
            </div>
          </section>

          {/* Lending Guidelines */}
          <section id="lending" className={styles.section}>
            <h2 className={styles.sectionTitle}>💰 Lending Guidelines</h2>
            <div className={styles.sectionContent}>
              <table className={styles.limitsTable}>
                <thead>
                  <tr>
                    <th>User Type</th>
                    <th>Max Amount</th>
                    <th>Tenure</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td>New User</td>
                    <td>₹50,000</td>
                    <td>12 months</td>
                  </tr>
                  <tr>
                    <td>Verified User</td>
                    <td>₹2,00,000</td>
                    <td>24 months</td>
                  </tr>
                  <tr>
                    <td>Premium User</td>
                    <td>₹5,00,000</td>
                    <td>36 months</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </section>

          {/* Dispute Resolution */}
          <section id="dispute" className={styles.section}>
            <h2 className={styles.sectionTitle}>⚖️ Dispute Resolution</h2>
            <div className={styles.sectionContent}>
              <ol>
                <li>Direct discussion between parties</li>
                <li>Optional platform mediation</li>
                <li>Arbitration under Indian law</li>
                <li>Legal action if required</li>
              </ol>

              <p>
                Arbitration shall be conducted in Bengaluru under the
                Arbitration & Conciliation Act, 1996.
              </p>
            </div>
          </section>
        </div>
      </div>
    </div>
  );
};

export default LegalPage;
